import hotkeys from 'hotkeys-js';
import type {CanvasEvents} from '@postermywall/fabricjs-2';
import {Canvas, Path, PencilBrush, Shadow} from '@postermywall/fabricjs-2';
import {KeyboardShortcutScope} from '@Libraries/keyboard-shortcuts';
import {logClientError} from '@Libraries/log.library';
import {TextOnPathExit, TextOnPathFlow} from '@Libraries/ga-events';
import {trackTextOnPathClosed, trackTextOnPathOpened} from '@Libraries/poster-editor-tracking.library';
import {getPosterUi} from '@PosterWhiteboard/ui/poster-ui';
import {getDrawingBrushCursor} from '@PosterWhiteboard/poster/poster-drawing/drawing-brush-cursor.library';
import {PENCIL_BRUSH} from '@PosterWhiteboard/models/draw.class';
import type {Poster} from '@PosterWhiteboard/poster/poster.class';
import type {Page} from '@PosterWhiteboard/page/page.class';
import type {TextItem} from '@PosterWhiteboard/items/text-item/text-item.class';
import type {DrawnPathInItemUnits} from '@PosterWhiteboard/items/text-item/text-shape-path.library';
import {getDrawnPathInItemUnits} from '@PosterWhiteboard/items/text-item/text-shape-path.library';
import {getTextShapeObjectForDrawnPath} from '@PosterWhiteboard/items/text-item/text-shape.class';
import type {TextOnPathDrawRequest, TextOnPathModeHistory} from './poster-text-on-path.types';
import {TEXT_ON_PATH_BRUSH_COLOR, TEXT_ON_PATH_BRUSH_HALO_COLOR, TEXT_ON_PATH_BRUSH_HALO_SCREEN_BLUR, TEXT_ON_PATH_BRUSH_SCREEN_WIDTH, TEXT_ON_PATH_REDRAW_MAX_FONT_GROWTH} from './poster-text-on-path.types';

interface CanvasInteractionSnapshot {
  selection: boolean;
  freeDrawingCursor: string;
}

/**
 * The Text on path drawing mode, after fabric's text-on-path demo: the user draws freeform strokes
 * on a page and each stroke becomes its own text item, "add your text" repeated from the stroke's
 * start to its end at the size every new text gets. Opened for an existing text item (the Shapes
 * block), each stroke redraws that item's path instead, its text kept and sized to fill the stroke.
 * Every stroke is one poster history entry, so undo and
 * redo while drawing take strokes back and put them back — scoped to this session, so they never
 * reach edits made before it. The mode owns the canvas's pointer input and the keyboard (Escape /
 * Enter finish) while open, like crop and refine; the panel reads the textOnPathMode slice.
 */
export class PosterTextOnPath {
  private page: Page | null = null;
  /** The existing item each stroke redraws; null when every stroke makes a new item. */
  private targetItemUid: string | null = null;
  /** The largest font a redraw may grow the existing item's text to, from the size it had when drawing opened, so redraws never compound. */
  private targetItemFontSizeCap: number | null = null;
  /** The items this session's strokes made or redrew, in order; undo may have removed some since. */
  private drawnItemUids: string[] = [];
  private sessionStartHistoryIndex = 0;
  /** The newest history entry this session's strokes made: redo stops here. */
  private sessionTopHistoryIndex = 0;
  /** Strokes apply one after another, so a quick second stroke is never dropped or interleaved. */
  private strokeQueue: Promise<void> = Promise.resolve();
  /** Strokes drawn but not yet turned into text. */
  private pendingStrokeCount = 0;
  private previousShortcutScope: string = KeyboardShortcutScope.POSTER_EDITOR;
  private canvasSnapshot: CanvasInteractionSnapshot | null = null;
  /** The pencil this session drew with; the canvas may have handed its brush to free-hand drawing since. */
  private brush: PencilBrush | null = null;

  public constructor(private readonly poster: Poster) {}

  public isActive(): boolean {
    return this.page !== null;
  }

  /** The item the newest stroke still on the page made or redrew, null before the first stroke (or after undoing them all). */
  public getLastDrawnItem(): TextItem | null {
    for (const uid of [...this.drawnItemUids].reverse()) {
      const item = this.page?.items.itemsHashMap[uid];
      if (item?.isText()) {
        return item;
      }
    }
    return null;
  }

  /**
   * Starts a drawing session on the page. One drawing mode at a time, the one started last: an open
   * text on path session for the other flow (redrawing a text's path, or drawing new text) ends
   * first with its paths kept, and an open free-hand drawing finishes first — its strokes become
   * their image item — and only then does this session take the canvas. Asking again for the
   * session already open changes nothing. Nothing happens while crop or refine is open.
   */
  public async enterDrawMode({page, item, source}: TextOnPathDrawRequest): Promise<void> {
    if (this.#isOpenFor(page, item)) {
      return;
    }
    if (this.isActive()) {
      await this.#exitOncePendingStrokesLand(TextOnPathExit.TAKEOVER);
    }
    if (this.poster.drawing.isDrawModeOn) {
      await this.poster.drawing.finishDrawMode();
    }
    const canvas = page.fabricCanvas;
    if (this.isActive() || this.poster.isOnCanvasEditModeActive() || this.poster.drawing.isDrawModeOn || !(canvas instanceof Canvas)) {
      return;
    }
    if (item !== undefined && item.page !== page) {
      return;
    }
    this.page = page;
    this.targetItemUid = item?.uid ?? null;
    this.targetItemFontSizeCap = item === undefined ? null : item.textStyles.fontSize * TEXT_ON_PATH_REDRAW_MAX_FONT_GROWTH;
    this.drawnItemUids = [];
    this.sessionStartHistoryIndex = this.poster.history.getCurrentHistoryIndex();
    this.sessionTopHistoryIndex = this.sessionStartHistoryIndex;
    this.poster.clearSelection();
    this.previousShortcutScope = hotkeys.getScope();
    hotkeys.setScope(KeyboardShortcutScope.TEXT_ON_PATH);
    this.poster.pages.setItemsInteractiveOnEveryPage(false);
    this.#startBrush(canvas);
    getPosterUi().enterTextOnPathMode({pageHashedId: page.hashedID, isRedrawingText: item !== undefined, ...this.#getHistoryState()});
    trackTextOnPathOpened(this.#getFlow(), source);
  }

  /** Ends the session once every drawn stroke has landed: the canvas goes back to selecting, and the newest item on a path is selected for editing. */
  public finishDrawing(): void {
    void this.#exitOncePendingStrokesLand(TextOnPathExit.FINISH).then((lastDrawnItem): void => {
      lastDrawnItem?.page.activeSelection.selectFabricObjects([lastDrawnItem.fabricObject]);
    });
  }

  /**
   * Ends the session for another action that takes over — an item being added, the Download dialog,
   * a click on the grey area around the page, free-hand drawing starting — as free-hand drawing
   * also ends then: every stroke drawn so far still lands, nothing is selected.
   */
  public stopDrawing(): void {
    void this.#exitOncePendingStrokesLand(TextOnPathExit.TAKEOVER);
  }

  /** True while a drawn stroke is being turned into text: the item that adds is the session's own, not another action. */
  public isPlacingText(): boolean {
    return this.pendingStrokeCount > 0;
  }

  /** The session already open is this one: the same page, and the same text to redraw or none. */
  #isOpenFor(page: Page, item: TextItem | undefined): boolean {
    return this.page === page && this.targetItemUid === (item?.uid ?? null);
  }

  /** Exits once the strokes already drawn have landed, and hands back the newest item on a path, if any. */
  async #exitOncePendingStrokesLand(exit: TextOnPathExit): Promise<TextItem | null> {
    const page = this.page;
    if (page === null) {
      return null;
    }
    await this.#waitForPendingStrokes();
    if (this.page !== page) {
      return null;
    }
    const lastDrawnItem = this.getLastDrawnItem();
    this.#exit(page, exit);
    return lastDrawnItem;
  }

  public dispose(): void {
    if (this.page !== null) {
      this.#exit(this.page, TextOnPathExit.TEARDOWN);
    }
  }

  /** Takes back the newest stroke of this session still on the page; never an edit made before the session. */
  public async undoStroke(): Promise<void> {
    await this.#waitForPendingStrokes();
    if (!this.#canUndoStroke()) {
      return;
    }
    await this.poster.history.undo();
    this.#publishHistoryState();
  }

  /** Puts back the stroke this session took back last. */
  public async redoStroke(): Promise<void> {
    await this.#waitForPendingStrokes();
    if (!this.#canRedoStroke()) {
      return;
    }
    await this.poster.history.redo();
    this.#publishHistoryState();
  }

  /**
   * The stroke fabric just finished on the page. It leaves the canvas at once (the text draws
   * along it instead) and joins the queue of strokes to put text on, in the order they were drawn.
   */
  public async onPathDrawn(e: CanvasEvents['path:created'], page: Page): Promise<void> {
    if (this.page !== page) {
      return;
    }
    const stroke = e.path;
    page.fabricCanvas.remove(stroke);
    if (!(stroke instanceof Path)) {
      return;
    }
    const strokeCommands = stroke.path;
    this.pendingStrokeCount += 1;
    this.strokeQueue = this.strokeQueue.then(async (): Promise<void> => this.#landStroke(page, strokeCommands));
    await this.strokeQueue;
  }

  /**
   * Puts text on one queued stroke. A stroke that fails is reported and dropped instead of
   * rejecting the queue: a rejected queue skips every later stroke and every exit that waits on it.
   */
  async #landStroke(page: Page, strokeCommands: (string | number)[][]): Promise<void> {
    try {
      if (this.page === page) {
        await this.#applyStroke(page, strokeCommands);
      }
    } catch (error) {
      void logClientError(error, {textOnPathStroke: true});
    } finally {
      this.pendingStrokeCount -= 1;
    }
  }

  /** Waits for every stroke drawn so far to land, including one drawn while waiting: the pencil stays on until the session exits. */
  async #waitForPendingStrokes(): Promise<void> {
    await this.strokeQueue;
    if (this.pendingStrokeCount > 0) {
      await this.#waitForPendingStrokes();
    }
  }

  async #applyStroke(page: Page, strokeCommands: (string | number)[][]): Promise<void> {
    const targetItem = this.#getTargetItem(page);
    // A new item starts unscaled and unrotated; an existing one draws its path through its own transform.
    const drawnPath = getDrawnPathInItemUnits(strokeCommands, targetItem?.fabricObject.calcTransformMatrix());
    if (drawnPath === null) {
      return;
    }
    const item = targetItem === null ? await this.#addPlaceholderItem(page, drawnPath) : await this.#redrawItem(targetItem, drawnPath);
    if (item === null) {
      return;
    }
    // The stroke is one history entry however many writes placed it, so undo takes the whole stroke back.
    this.poster.history.addPosterHistory();
    this.sessionTopHistoryIndex = this.poster.history.getCurrentHistoryIndex();
    this.drawnItemUids.push(item.uid);
    this.#publishHistoryState();
  }

  /** A new item with the placeholder running the stroke from its start to its end, placed on it. */
  async #addPlaceholderItem(page: Page, drawnPath: DrawnPathInItemUnits): Promise<TextItem | null> {
    const item = await page.items.addItems.addTextOnPathItem(drawnPath.path, {left: drawnPath.left, top: drawnPath.top});
    if (item === null) {
      return null;
    }
    await this.#moveItemOntoStroke(item, drawnPath);
    return item;
  }

  /** The existing item's path becomes the stroke, its text kept and sized to run the stroke from start to end. */
  async #redrawItem(item: TextItem, drawnPath: DrawnPathInItemUnits): Promise<TextItem> {
    await item.updateFromObject({shape: getTextShapeObjectForDrawnPath(drawnPath.path)}, {undoable: false});
    await this.#sizeTextToFillStroke(item);
    await this.#moveItemOntoStroke(item, drawnPath);
    return item;
  }

  /**
   * The stroke drawn for an existing text is a size request for it: the font grows or shrinks once,
   * as the stroke lands, so the text runs it end to end (no larger than the cap). From then on the
   * usual rules hold — more text shrinks, less text keeps its size, the Size control works.
   */
  async #sizeTextToFillStroke(item: TextItem): Promise<void> {
    const cap = this.targetItemFontSizeCap;
    const fontSize = cap === null ? null : item.getFontSizeFillingDrawnPath(cap);
    if (fontSize === null || fontSize === item.textStyles.fontSize) {
      return;
    }
    await item.updateFromObject({textStyles: {fontSize}}, {undoable: false});
  }

  /** The item a Shapes-block session redraws, looked up afresh (an undo or redo may have replaced its instance); null for a session that makes new items. */
  #getTargetItem(page: Page): TextItem | null {
    if (this.targetItemUid === null) {
      return null;
    }
    const item = page.items.itemsHashMap[this.targetItemUid];
    return item?.isText() ? item : null;
  }

  /**
   * The shaped layout centres the path and glyphs in the item's box, so the path's origin lands
   * wherever that box puts it. The item moves by the difference between where the origin rendered
   * and where the stroke's top-left was drawn, so the text runs along the stroke itself.
   */
  async #moveItemOntoStroke(item: TextItem, drawnPath: DrawnPathInItemUnits): Promise<void> {
    const renderedOrigin = item.getDrawnPathOriginOnPage();
    if (renderedOrigin === null) {
      return;
    }
    const deltaX = drawnPath.left - renderedOrigin.x;
    const deltaY = drawnPath.top - renderedOrigin.y;
    if (Math.abs(deltaX) < Number.EPSILON && Math.abs(deltaY) < Number.EPSILON) {
      return;
    }
    await item.updateFromObject({left: item.fabricObject.left + deltaX, top: item.fabricObject.top + deltaY}, {undoable: false});
  }

  #canUndoStroke(): boolean {
    return this.poster.history.getCurrentHistoryIndex() > this.sessionStartHistoryIndex;
  }

  #canRedoStroke(): boolean {
    return this.poster.history.getCurrentHistoryIndex() < this.sessionTopHistoryIndex;
  }

  #getHistoryState(): TextOnPathModeHistory {
    return {canUndo: this.#canUndoStroke(), canRedo: this.#canRedoStroke()};
  }

  #publishHistoryState(): void {
    if (this.page !== null) {
      getPosterUi().updateTextOnPathMode(this.#getHistoryState());
    }
  }

  #startBrush(canvas: Canvas): void {
    this.canvasSnapshot = {selection: canvas.selection, freeDrawingCursor: canvas.freeDrawingCursor};
    const brush = new PencilBrush(canvas);
    brush.color = TEXT_ON_PATH_BRUSH_COLOR;
    brush.width = TEXT_ON_PATH_BRUSH_SCREEN_WIDTH / canvas.getZoom();
    // Fabric scales a brush shadow by the zoom as it draws, so the halo is given in page units like the width.
    brush.shadow = new Shadow({color: TEXT_ON_PATH_BRUSH_HALO_COLOR, blur: TEXT_ON_PATH_BRUSH_HALO_SCREEN_BLUR / canvas.getZoom(), offsetX: 0, offsetY: 0});
    canvas.freeDrawingBrush = brush;
    this.brush = brush;
    canvas.freeDrawingCursor = getDrawingBrushCursor(PENCIL_BRUSH);
    canvas.selection = false;
    canvas.isDrawingMode = true;
  }

  #stopBrush(canvas: Canvas): void {
    // Free-hand drawing started after this session has the canvas's pencil now: it stays drawing.
    const isStillThisSessionsPencil = canvas.freeDrawingBrush === this.brush;
    if (isStillThisSessionsPencil) {
      canvas.isDrawingMode = false;
      canvas.freeDrawingBrush = undefined;
    }
    if (this.canvasSnapshot !== null) {
      canvas.selection = this.canvasSnapshot.selection;
      if (isStillThisSessionsPencil) {
        canvas.freeDrawingCursor = this.canvasSnapshot.freeDrawingCursor;
      }
      this.canvasSnapshot = null;
    }
    this.brush = null;
  }

  #getFlow(): TextOnPathFlow {
    return this.targetItemUid === null ? TextOnPathFlow.NEW : TextOnPathFlow.REDRAW;
  }

  /** Every stroke is one history entry and undo never reaches below the session's start, so the entries above it are the paths still on the page. */
  #getPathsKept(): number {
    return Math.max(0, this.poster.history.getCurrentHistoryIndex() - this.sessionStartHistoryIndex);
  }

  #exit(page: Page, exit: TextOnPathExit): void {
    trackTextOnPathClosed({flow: this.#getFlow(), pathsKept: this.#getPathsKept(), hasDrawn: this.drawnItemUids.length > 0, exit});
    const canvas = page.fabricCanvas;
    if (canvas instanceof Canvas) {
      this.#stopBrush(canvas);
    }
    this.poster.pages.setItemsInteractiveOnEveryPage(true);
    hotkeys.setScope(this.previousShortcutScope);
    this.page = null;
    this.targetItemUid = null;
    this.targetItemFontSizeCap = null;
    this.drawnItemUids = [];
    getPosterUi().exitTextOnPathMode();
  }

}

